import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/constants/branding";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Check, Wand2, LogOut, BarChart3, Globe, Users as UsersIcon, Eye, MapPin, Image as ImageIcon, ChevronDown } from "lucide-react";

function useResource(path) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(path);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  return { items, loading, reload: load };
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CLBC" className="w-9 h-9 rounded-full ring-1 ring-gold/40" />
            <div className="text-xs uppercase tracking-[0.25em] text-gold-500 font-semibold">CLBC Admin</div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-500 hidden sm:block">{user.email}</div>
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="btn-ghost text-sm"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-10">
        <h1 className="font-serif text-4xl text-royal">Content Management</h1>
        <p className="text-slate-500 mt-2">Manage all site content in one place.</p>

        <Tabs defaultValue="sermons" className="mt-8">
          <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent p-0">
            {["analytics","sermons","series","events","devotionals","ministries","leaders","gallery","announcements","testimonies","prayers","homepage"].map((t) => (
              <TabsTrigger
                key={t}
                value={t}
                data-testid={`admin-tab-${t}`}
                className="rounded-full px-5 py-2 data-[state=active]:bg-royal data-[state=active]:text-white bg-white border border-slate-200 capitalize"
              >
                {t}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="analytics" className="mt-6"><AnalyticsPanel /></TabsContent>
          <TabsContent value="sermons" className="mt-6"><SermonsAdmin /></TabsContent>
          <TabsContent value="series" className="mt-6"><SeriesAdmin /></TabsContent>
          <TabsContent value="events" className="mt-6"><EventsAdmin /></TabsContent>
          <TabsContent value="devotionals" className="mt-6"><DevotionalsAdmin /></TabsContent>
          <TabsContent value="ministries" className="mt-6"><MinistriesAdmin /></TabsContent>
          <TabsContent value="leaders" className="mt-6"><LeadersAdmin /></TabsContent>
          <TabsContent value="gallery" className="mt-6"><GalleryAdmin /></TabsContent>
          <TabsContent value="announcements" className="mt-6"><AnnouncementsAdmin /></TabsContent>
          <TabsContent value="testimonies" className="mt-6"><TestimoniesAdmin /></TabsContent>
          <TabsContent value="prayers" className="mt-6"><PrayersAdmin /></TabsContent>
          <TabsContent value="homepage" className="mt-6"><HomepageAdmin /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function CrudCard({ title, children, actions, testId }) {
  return (
    <div className="card-soft p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">{children}</div>
        <div className="flex gap-2 flex-shrink-0">{actions}</div>
      </div>
    </div>
  );
}

/* ---------- Analytics ---------- */
function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/analytics", { params: { days: d } });
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); const t = setInterval(() => load(days), 30000); return () => clearInterval(t); }, [days]);

  if (loading && !data) return <div className="text-slate-500 py-10">Loading analytics…</div>;
  if (!data) return null;

  const maxDay = Math.max(1, ...(data.per_day || []).map((d) => d.count));
  const flagFor = (cc) => cc ? String.fromCodePoint(...cc.toUpperCase().split("").map(c => 127397 + c.charCodeAt(0))) : "🌐";

  return (
    <div className="space-y-6" data-testid="admin-analytics-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-royal">Site Analytics</h2>
          <p className="text-sm text-slate-500">Live visitor stats · auto-refreshing every 30s</p>
        </div>
        <div className="inline-flex bg-white rounded-full border border-slate-200 p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`analytics-range-${d}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                days === d ? "bg-royal text-white" : "text-slate-600 hover:text-royal"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <StatCard icon={Eye} label="Total Visits (all-time)" value={data.total_visits} accent="royal" testId="stat-total" />
        <StatCard icon={BarChart3} label={`Visits (last ${data.days}d)`} value={data.period_visits} accent="gold" testId="stat-period" />
        <StatCard icon={UsersIcon} label={`Unique Visitors (${data.days}d)`} value={data.unique_visitors} accent="royal" testId="stat-unique" />
      </div>

      <div className="card-soft p-6" data-testid="analytics-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold-500">Visits per day</div>
            <div className="font-serif text-xl text-royal">Last {data.days} days</div>
          </div>
        </div>
        <div className="flex items-end gap-1 h-40">
          {(data.per_day || []).map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.count}`}>
              <div className="w-full bg-gradient-to-t from-royal to-royal-500 rounded-t-md hover:from-gold hover:to-gold-500 transition-colors" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: "3px" }} />
            </div>
          ))}
          {(data.per_day || []).length === 0 && <div className="w-full text-center text-slate-400 text-sm">No visits yet.</div>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-soft p-6" data-testid="analytics-countries">
          <div className="flex items-center gap-2 mb-5">
            <Globe className="w-4 h-4 text-gold" />
            <div className="font-serif text-lg text-royal">Top Countries</div>
          </div>
          <ul className="space-y-3">
            {(data.top_countries || []).map((c, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-3">
                  <span className="text-xl">{flagFor(c.country_code)}</span>
                  <span className="text-slate-700">{c.country}</span>
                </span>
                <span className="font-semibold text-royal">{c.count}</span>
              </li>
            ))}
            {(data.top_countries || []).length === 0 && <li className="text-slate-400 text-sm">No data yet.</li>}
          </ul>
        </div>

        <div className="card-soft p-6" data-testid="analytics-cities">
          <div className="flex items-center gap-2 mb-5">
            <MapPin className="w-4 h-4 text-gold" />
            <div className="font-serif text-lg text-royal">Top Cities</div>
          </div>
          <ul className="space-y-3">
            {(data.top_cities || []).map((c, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{c.city}<span className="text-slate-400 text-xs ml-2">{c.country}</span></span>
                <span className="font-semibold text-royal">{c.count}</span>
              </li>
            ))}
            {(data.top_cities || []).length === 0 && <li className="text-slate-400 text-sm">No data yet.</li>}
          </ul>
        </div>
      </div>

      <div className="card-soft p-6" data-testid="analytics-pages">
        <div className="font-serif text-lg text-royal mb-5">Top Pages</div>
        <ul className="space-y-2">
          {(data.top_paths || []).map((p, i) => (
            <li key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
              <span className="text-slate-700 font-mono text-xs">{p.path}</span>
              <span className="font-semibold text-royal">{p.count}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="card-soft p-6" data-testid="analytics-recent">
        <div className="font-serif text-lg text-royal mb-5">Recent Visitors</div>
        <div className="space-y-2">
          {(data.recent || []).map((v, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
              <div className="flex items-center gap-3">
                <span className="text-xl">{flagFor(v.country_code)}</span>
                <div>
                  <div className="text-slate-700">{v.city || "Unknown"}, <span className="text-slate-500">{v.country || "Unknown"}</span></div>
                  <div className="text-xs text-slate-400 font-mono">{v.ip} · {v.path}</div>
                </div>
              </div>
              <div className="text-xs text-slate-500">{new Date(v.created_at).toLocaleString()}</div>
            </div>
          ))}
          {(data.recent || []).length === 0 && <div className="text-slate-400 text-sm">No visitors yet.</div>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testId }) {
  const bg = accent === "gold" ? "from-gold-500 to-gold" : "from-royal to-royal-700";
  return (
    <div className="card-soft p-6" data-testid={testId}>
      <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${bg} text-white flex items-center justify-center shadow-md`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-5 text-3xl font-serif text-royal">{value.toLocaleString()}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

/* ---------- Sermons ---------- */
function SermonsAdmin() {
  const { items, reload } = useResource("/sermons");
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/sermons", {
        title: fd.get("title"),
        preacher: fd.get("preacher") || "Apostle Percy Konotey",
        date: fd.get("date"),
        scripture: fd.get("scripture") || null,
        description: fd.get("description") || null,
        youtube_id: fd.get("youtube_id") || null,
        audio_url: fd.get("audio_url") || null,
        notes_url: fd.get("notes_url") || null,
        thumbnail: fd.get("thumbnail") || null,
        series: fd.get("series") || null,
      });
      toast.success("Sermon added");
      setOpen(false);
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this sermon?")) return;
    await api.delete(`/admin/sermons/${id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary text-sm py-2.5" data-testid="admin-sermons-add"><Plus className="w-4 h-4" /> Add Sermon</button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New Sermon</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div><Label>Title</Label><Input name="title" required data-testid="admin-sermon-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" name="date" required /></div>
                <div><Label>Series</Label><Input name="series" /></div>
              </div>
              <div><Label>Preacher</Label><Input name="preacher" defaultValue="Apostle Percy Konotey" /></div>
              <div><Label>Scripture</Label><Input name="scripture" placeholder="John 3:16" /></div>
              <div><Label>YouTube Video ID</Label><Input name="youtube_id" placeholder="dQw4w9WgXcQ" /></div>
              <div><Label>Audio recording URL (MP3)</Label><Input name="audio_url" placeholder="https://…/sermon.mp3" data-testid="admin-sermon-audio" /></div>
              <div><Label>Sermon notes URL (PDF)</Label><Input name="notes_url" placeholder="https://…/notes.pdf" data-testid="admin-sermon-notes" /></div>
              <div><Label>Custom thumbnail URL (optional)</Label><Input name="thumbnail" placeholder="https://…/thumb.jpg" /></div>
              <div><Label>Description</Label><Textarea name="description" rows={4} /></div>
              <DialogFooter><button className="btn-primary" data-testid="admin-sermon-submit">Save</button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.map((s) => (
        <CrudCard
          key={s.id}
          testId={`admin-sermon-${s.id}`}
          actions={<Button variant="ghost" size="icon" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
        >
          <div className="text-xs uppercase tracking-widest text-gold-500">{s.series || "Sermon"} · {new Date(s.date).toLocaleDateString()}</div>
          <div className="font-serif text-lg text-royal">{s.title}</div>
          <div className="text-sm text-slate-500">{s.preacher}</div>
        </CrudCard>
      ))}
    </div>
  );
}

/* ---------- Sermon Series ---------- */
function SeriesAdmin() {
  const { items, reload } = useResource("/series");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const body = {
      name: fd.get("name"),
      tagline: fd.get("tagline") || null,
      description: fd.get("description") || null,
      cover_image: fd.get("cover_image") || null,
      order: parseInt(fd.get("order") || "0", 10),
    };
    try {
      if (editing) {
        await api.put(`/admin/series/${editing.id}`, body);
        toast.success("Series updated");
      } else {
        await api.post("/admin/series", body);
        toast.success("Series added");
      }
      setOpen(false); setEditing(null); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this series (sermons will remain)?")) return;
    await api.delete(`/admin/series/${id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Give each sermon series a cover banner. Netflix-style rows appear on the public Sermons page.</p>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <button className="btn-primary text-sm py-2.5" data-testid="admin-series-add"><Plus className="w-4 h-4" /> Add Series</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit series" : "New series"}</DialogTitle>
              <DialogDescription>Series names must match the "Series" field on sermons to link them.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div><Label>Name</Label><Input name="name" required defaultValue={editing?.name || ""} data-testid="admin-series-name" /></div>
              <div><Label>Tagline (optional)</Label><Input name="tagline" defaultValue={editing?.tagline || ""} /></div>
              <div>
                <Label>Cover image URL</Label>
                <Input name="cover_image" defaultValue={editing?.cover_image || ""} placeholder="https://…/banner.jpg" data-testid="admin-series-cover" />
                <p className="text-[11px] text-slate-400 mt-1">Landscape image works best (1600×600).</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Order</Label><Input type="number" name="order" defaultValue={editing?.order ?? 0} /></div>
              </div>
              <div><Label>Description</Label><Textarea name="description" rows={3} defaultValue={editing?.description || ""} /></div>
              <DialogFooter><button className="btn-primary">{editing ? "Save changes" : "Create series"}</button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-slate-500 py-10 text-center">No series yet. Click "Add Series" to create your first playlist.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((s) => (
            <div key={s.id} className="card-soft overflow-hidden" data-testid={`admin-series-${s.id}`}>
              <div className="relative aspect-[16/6] bg-royal-700">
                {s.cover_image ? (
                  <img src={s.cover_image} alt={s.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40"><ImageIcon className="w-8 h-8" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-royal-800/80 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-4 text-white">
                  <div className="text-[10px] uppercase tracking-widest text-gold">Series</div>
                  <div className="font-serif text-xl">{s.name}</div>
                  {s.tagline && <div className="text-xs text-white/80 italic mt-1 line-clamp-1">"{s.tagline}"</div>}
                </div>
              </div>
              <div className="p-4 flex items-center justify-between text-xs text-slate-500">
                <span>Order: {s.order ?? 0}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Events ---------- */
function EventsAdmin() {
  const { items, reload } = useResource("/events");
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [regsByEvent, setRegsByEvent] = useState({});

  const loadCounts = async () => {
    try {
      const { data } = await api.get("/admin/events/registration-counts");
      setCounts(data || {});
    } catch {}
  };
  useEffect(() => { loadCounts(); }, [items.length]);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/events", {
        title: fd.get("title"),
        description: fd.get("description") || null,
        starts_at: new Date(fd.get("starts_at")).toISOString(),
        location: fd.get("location") || null,
        registration_required: fd.get("registration") === "on",
      });
      toast.success("Event added");
      setOpen(false); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`); reload();
  };

  const toggleRegs = async (ev) => {
    if (expanded === ev.id) { setExpanded(null); return; }
    setExpanded(ev.id);
    if (!regsByEvent[ev.id]) {
      try {
        const { data } = await api.get(`/admin/events/${ev.id}/registrations`);
        setRegsByEvent((p) => ({ ...p, [ev.id]: data }));
      } catch (err) { toast.error("Could not load registrations"); }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary text-sm py-2.5" data-testid="admin-events-add"><Plus className="w-4 h-4" /> Add Event</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Event</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div><Label>Title</Label><Input name="title" required /></div>
              <div><Label>Description</Label><Textarea name="description" rows={3} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Starts at</Label><Input type="datetime-local" name="starts_at" required /></div>
                <div><Label>Location</Label><Input name="location" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="registration" /> Requires registration</label>
              <DialogFooter><button className="btn-primary">Save</button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.map((ev) => {
        const c = counts[ev.id] || 0;
        const isOpen = expanded === ev.id;
        const regs = regsByEvent[ev.id] || [];
        return (
          <div key={ev.id} className="card-soft p-6" data-testid={`admin-event-${ev.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-gold-500">{new Date(ev.starts_at).toLocaleString()}</div>
                <div className="font-serif text-lg text-royal">{ev.title}</div>
                <div className="text-sm text-slate-500">{ev.location}</div>
                {ev.registration_required && (
                  <button
                    onClick={() => toggleRegs(ev)}
                    className="mt-3 inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-royal/5 text-royal hover:bg-royal/10 transition-colors"
                    data-testid={`admin-event-regs-toggle-${ev.id}`}
                  >
                    <UsersIcon className="w-3.5 h-3.5" />
                    {c} {c === 1 ? "registration" : "registrations"}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(ev.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
            {isOpen && (
              <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`admin-event-regs-list-${ev.id}`}>
                {regs.length === 0 ? (
                  <div className="text-sm text-slate-500 py-4">No registrations yet.</div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs uppercase tracking-widest text-gold-500">RSVP List · {regs.length}</div>
                      <button
                        onClick={() => downloadCsv(ev, regs)}
                        className="text-xs font-medium text-royal hover:underline"
                      >
                        Download CSV
                      </button>
                    </div>
                    <div className="space-y-2">
                      {regs.map((r) => (
                        <div key={r.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                          <div>
                            <div className="text-royal font-medium">{r.name}</div>
                            <div className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ""}</div>
                          </div>
                          <div className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function downloadCsv(ev, regs) {
  const rows = [["Name", "Email", "Phone", "Registered At"], ...regs.map((r) => [r.name, r.email, r.phone || "", r.created_at])];
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${ev.title.replace(/[^a-z0-9]+/gi, "-")}-rsvps.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- Devotionals ---------- */
function DevotionalsAdmin() {
  const { items, reload } = useResource("/devotionals");
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  const runAI = async (e) => {
    e.preventDefault();
    setAiBusy(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await api.post("/admin/devotionals/ai-draft", {
        topic: fd.get("topic"),
        scripture_ref: fd.get("scripture_ref") || null,
        tone: fd.get("tone") || "warm and pastoral",
      });
      setDraft(data);
      toast.success("Draft ready. Edit and save below.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/devotionals", {
        title: fd.get("title"),
        scripture: fd.get("scripture"),
        scripture_ref: fd.get("scripture_ref") || null,
        body: fd.get("body"),
        prayer: fd.get("prayer") || null,
        date: fd.get("date"),
        published: true,
      });
      toast.success("Devotional saved");
      setOpen(false); setDraft(null); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this devotional?")) return;
    await api.delete(`/admin/devotionals/${id}`); reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-3">
        <Dialog open={aiOpen} onOpenChange={setAiOpen}>
          <DialogTrigger asChild>
            <button className="btn-secondary text-sm py-2.5" data-testid="admin-devotional-ai"><Wand2 className="w-4 h-4" /> AI Draft</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4 text-gold-500" /> AI Devotional Draft</DialogTitle>
              <DialogDescription>Powered by Claude Sonnet 4.5. Generate a starting point — you can edit before publishing.</DialogDescription>
            </DialogHeader>
            <form onSubmit={runAI} className="grid gap-4">
              <div><Label>Topic</Label><Input name="topic" required placeholder="Walking in the fear of the Lord" data-testid="admin-ai-topic" /></div>
              <div><Label>Preferred scripture reference (optional)</Label><Input name="scripture_ref" placeholder="Proverbs 9:10" /></div>
              <div><Label>Tone</Label><Input name="tone" defaultValue="warm and pastoral" /></div>
              <DialogFooter>
                <button disabled={aiBusy} className="btn-primary" data-testid="admin-ai-generate">
                  {aiBusy ? "Generating…" : "Generate Draft"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={open || !!draft} onOpenChange={(v) => { setOpen(v); if (!v) setDraft(null); }}>
          <DialogTrigger asChild>
            <button className="btn-primary text-sm py-2.5" data-testid="admin-devotional-add"><Plus className="w-4 h-4" /> Add Devotional</button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{draft ? "Review AI Draft" : "New Devotional"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" name="date" required defaultValue={new Date().toISOString().slice(0,10)} /></div>
                <div><Label>Title</Label><Input name="title" required defaultValue={draft?.title || ""} data-testid="admin-devotional-title" /></div>
              </div>
              <div><Label>Scripture text</Label><Textarea name="scripture" required rows={2} defaultValue={draft?.scripture || ""} /></div>
              <div><Label>Scripture reference</Label><Input name="scripture_ref" defaultValue={draft?.scripture_ref || ""} /></div>
              <div><Label>Body</Label><Textarea name="body" required rows={7} defaultValue={draft?.body || ""} /></div>
              <div><Label>Prayer</Label><Textarea name="prayer" rows={3} defaultValue={draft?.prayer || ""} /></div>
              <DialogFooter><button className="btn-primary" data-testid="admin-devotional-submit">Publish</button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.map((d) => (
        <CrudCard key={d.id} testId={`admin-devotional-${d.id}`}
          actions={<Button variant="ghost" size="icon" onClick={() => del(d.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}>
          <div className="text-xs uppercase tracking-widest text-gold-500">{new Date(d.date).toLocaleDateString()} · {d.scripture_ref}</div>
          <div className="font-serif text-lg text-royal">{d.title}</div>
          <div className="text-sm text-slate-500 line-clamp-2">{d.body}</div>
        </CrudCard>
      ))}
    </div>
  );
}

/* ---------- Ministries ---------- */
function MinistriesAdmin() {
  const { items, reload } = useResource("/ministries");
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    await api.post("/admin/ministries", {
      name: fd.get("name"),
      tagline: fd.get("tagline") || null,
      description: fd.get("description"),
      icon: fd.get("icon") || "Users",
      leader: fd.get("leader") || null,
    });
    toast.success("Ministry added"); setOpen(false); reload();
  };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/ministries/${id}`); reload(); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button className="btn-primary text-sm py-2.5"><Plus className="w-4 h-4" /> Add Ministry</button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Ministry</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div><Label>Name</Label><Input name="name" required /></div>
              <div><Label>Tagline</Label><Input name="tagline" /></div>
              <div><Label>Icon (Users, Music, Baby, Heart, Shield, Globe)</Label><Input name="icon" defaultValue="Users" /></div>
              <div><Label>Leader</Label><Input name="leader" /></div>
              <div><Label>Description</Label><Textarea name="description" required rows={4} /></div>
              <DialogFooter><button className="btn-primary">Save</button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.map((m) => (
        <CrudCard key={m.id} testId={`admin-ministry-${m.id}`}
          actions={<Button variant="ghost" size="icon" onClick={() => del(m.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}>
          <div className="font-serif text-lg text-royal">{m.name}</div>
          <div className="text-xs text-gold-500">{m.tagline}</div>
          <div className="text-sm text-slate-500 line-clamp-2 mt-1">{m.description}</div>
        </CrudCard>
      ))}
    </div>
  );
}

/* ---------- Leaders ---------- */
function LeadersAdmin() {
  const { items, reload } = useResource("/leaders");
  const [open, setOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    await api.post("/admin/leaders", {
      name: fd.get("name"),
      title: fd.get("title"),
      bio: fd.get("bio") || null,
      image: fd.get("image") || null,
      order: parseInt(fd.get("order") || "0", 10),
    });
    toast.success("Leader added"); setOpen(false); reload();
  };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/leaders/${id}`); reload(); } };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button className="btn-primary text-sm py-2.5"><Plus className="w-4 h-4" /> Add Leader</button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Leader</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div><Label>Name</Label><Input name="name" required /></div>
              <div><Label>Title</Label><Input name="title" required /></div>
              <div><Label>Image URL</Label><Input name="image" /></div>
              <div><Label>Order</Label><Input type="number" name="order" defaultValue="0" /></div>
              <div><Label>Bio</Label><Textarea name="bio" rows={4} /></div>
              <DialogFooter><button className="btn-primary">Save</button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {items.map((l) => (
        <CrudCard key={l.id} testId={`admin-leader-${l.id}`}
          actions={<Button variant="ghost" size="icon" onClick={() => del(l.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}>
          <div className="text-xs uppercase tracking-widest text-gold-500">{l.title}</div>
          <div className="font-serif text-lg text-royal">{l.name}</div>
        </CrudCard>
      ))}
    </div>
  );
}

/* ---------- Gallery ---------- */
function GalleryAdmin() {
  const { items, reload } = useResource("/gallery");
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/gallery", {
        title: fd.get("title") || null,
        image: fd.get("image"),
        category: fd.get("category") || "General",
      });
      toast.success("Photo added to gallery");
      setOpen(false);
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const uploadFiles = async (files, sharedTitle, category) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of list) {
      const form = new FormData();
      form.append("file", f);
      const params = new URLSearchParams();
      if (sharedTitle) params.set("title", sharedTitle);
      if (category) params.set("category", category);
      try {
        await api.post(`/admin/gallery/upload?${params.toString()}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        fail++;
        console.error(err);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded`);
    if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed`);
    if (ok) { setOpen(false); reload(); }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const files = e.target.elements.files.files;
    await uploadFiles(files, fd.get("title") || null, fd.get("category") || "General");
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    await uploadFiles(e.dataTransfer.files, null, "General");
  };

  const del = async (id) => {
    if (!confirm("Remove this photo?")) return;
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Removed");
    reload();
  };

  return (
    <div className="space-y-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
          dragActive ? "border-gold bg-gold/5" : "border-slate-200 bg-slate-50/50"
        }`}
        data-testid="admin-gallery-dropzone"
      >
        <div className="font-serif text-lg text-royal">Drag & drop photos here</div>
        <div className="text-sm text-slate-500 mt-1">or use the button below to upload from your device</div>
        <div className="mt-4 flex flex-wrap gap-3 justify-center">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary text-sm py-2.5" data-testid="admin-gallery-add">
                <Plus className="w-4 h-4" /> Upload Photos
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Gallery Photos</DialogTitle>
                <DialogDescription>Upload from your device or paste a hosted image URL.</DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="upload" className="mt-2">
                <TabsList className="grid grid-cols-2 mb-4">
                  <TabsTrigger value="upload" data-testid="admin-gallery-tab-upload">Upload</TabsTrigger>
                  <TabsTrigger value="url" data-testid="admin-gallery-tab-url">From URL</TabsTrigger>
                </TabsList>

                <TabsContent value="upload">
                  <form onSubmit={submitUpload} className="grid gap-4">
                    <div>
                      <Label>Choose images (JPG / PNG / WEBP — up to 10MB each)</Label>
                      <Input
                        type="file"
                        name="files"
                        accept="image/*"
                        multiple
                        required
                        data-testid="admin-gallery-file-input"
                      />
                    </div>
                    <div>
                      <Label>Title (optional — applied to all)</Label>
                      <Input name="title" placeholder="e.g. Sunday Worship" data-testid="admin-gallery-upload-title" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input name="category" defaultValue="Services" placeholder="Services / Prayer / Youth …" data-testid="admin-gallery-upload-category" />
                    </div>
                    <DialogFooter>
                      <button
                        type="submit"
                        className="btn-primary"
                        disabled={uploading}
                        data-testid="admin-gallery-upload-submit"
                      >
                        {uploading ? "Uploading…" : "Upload"}
                      </button>
                    </DialogFooter>
                  </form>
                </TabsContent>

                <TabsContent value="url">
                  <form onSubmit={submit} className="grid gap-4">
                    <div><Label>Image URL</Label><Input name="image" required placeholder="https://…" data-testid="admin-gallery-image" /></div>
                    <div><Label>Title (optional)</Label><Input name="title" data-testid="admin-gallery-title" /></div>
                    <div><Label>Category</Label><Input name="category" defaultValue="Services" placeholder="Services / Prayer / Youth …" /></div>
                    <DialogFooter><button className="btn-primary" data-testid="admin-gallery-submit">Save</button></DialogFooter>
                  </form>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center text-slate-500 py-16">No photos yet. Upload your first one above.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((g) => (
            <div key={g.id} className="card-soft overflow-hidden group relative" data-testid={`admin-gallery-${g.id}`}>
              <div className="aspect-square overflow-hidden bg-slate-100">
                <img src={g.image} alt={g.title || "Gallery"} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <div className="text-[10px] uppercase tracking-widest text-gold-500">{g.category || "General"}</div>
                <div className="text-sm text-royal font-serif line-clamp-1">{g.title || "Untitled"}</div>
              </div>
              <button
                onClick={() => del(g.id)}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete"
                data-testid={`admin-gallery-delete-${g.id}`}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Announcements ---------- */
function AnnouncementsAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/announcements");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/admin/announcements", {
        title: fd.get("title"),
        body: fd.get("body"),
        active: true,
      });
      toast.success("Announcement published");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggle = async (a) => {
    await api.patch(`/admin/announcements/${a.id}`, { active: !a.active });
    toast.success(a.active ? "Hidden from homepage" : "Now live on homepage");
    load();
  };

  const del = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-serif text-xl text-royal">Homepage Announcement Banner</h3>
          <p className="text-sm text-slate-500 mt-1">Active announcements appear as a dismissible banner at the top of the home page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary text-sm py-2.5" data-testid="admin-announcement-add">
              <Plus className="w-4 h-4" /> New Announcement
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Announcement</DialogTitle>
              <DialogDescription>Keep it short — a title and a one-line description work best.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid gap-4">
              <div>
                <Label>Title</Label>
                <Input name="title" required placeholder="Special Sunday Service" data-testid="admin-announcement-title" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea name="body" required rows={3} placeholder="Join us this Sunday at 10am for a night of worship." data-testid="admin-announcement-body" />
              </div>
              <DialogFooter>
                <button className="btn-primary" data-testid="admin-announcement-submit">Publish</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="text-center text-slate-500 py-16 card-soft">
          No announcements yet. Create one to spotlight an upcoming service or event.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="card-soft p-5 flex items-start justify-between gap-4" data-testid={`admin-announcement-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {a.active ? (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-green-100 text-green-700 font-semibold">Live</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-slate-200 text-slate-600 font-semibold">Hidden</span>
                  )}
                  <div className="font-serif text-lg text-royal truncate">{a.title}</div>
                </div>
                <p className="mt-2 text-slate-700 text-sm leading-relaxed">{a.body}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant={a.active ? "outline" : "default"}
                  onClick={() => toggle(a)}
                  data-testid={`admin-announcement-toggle-${a.id}`}
                  className={a.active ? "" : "bg-royal hover:bg-royal-700"}
                >
                  {a.active ? "Hide" : "Activate"}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => del(a.id)}
                  data-testid={`admin-announcement-delete-${a.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Testimonies ---------- */
function TestimoniesAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/testimonies"); setItems(data); };
  useEffect(() => { load(); }, []);

  const approve = async (id) => { await api.post(`/admin/testimonies/${id}/approve`); toast.success("Approved"); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/testimonies/${id}`); load(); } };

  return (
    <div className="space-y-4">
      {items.map((t) => (
        <CrudCard key={t.id} testId={`admin-testimony-${t.id}`}
          actions={
            <div className="flex gap-2">
              {!t.approved && <Button size="sm" onClick={() => approve(t.id)} className="bg-royal hover:bg-royal-700"><Check className="w-4 h-4" /></Button>}
              <Button variant="ghost" size="icon" onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          }>
          <div className="flex items-center gap-3">
            <div className="font-serif text-lg text-royal">{t.name}</div>
            {t.approved ? <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Approved</span>
              : <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">Pending</span>}
          </div>
          <div className="text-sm text-slate-500 mt-1">{t.location}</div>
          <p className="mt-2 text-slate-700">{t.content}</p>
        </CrudCard>
      ))}
    </div>
  );
}

/* ---------- Prayers ---------- */
function PrayersAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get("/admin/prayers"); setItems(data); };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.put(`/admin/prayers/${id}/status`, null, { params: { status } }); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/prayers/${id}`); load(); } };

  return (
    <div className="space-y-4">
      {items.map((p) => (
        <CrudCard key={p.id} testId={`admin-prayer-${p.id}`}
          actions={
            <div className="flex gap-2 items-center">
              <select
                value={p.status}
                onChange={(e) => setStatus(p.id, e.target.value)}
                className="text-xs border border-slate-200 rounded-full px-3 py-1"
              >
                <option value="pending">Pending</option>
                <option value="praying">Praying</option>
                <option value="answered">Answered</option>
              </select>
              <Button variant="ghost" size="icon" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          }>
          <div className="flex items-center gap-3">
            <div className="font-serif text-lg text-royal">{p.name}</div>
            {p.is_private && <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">Private</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">{p.email} · {p.phone}</div>
          <p className="mt-2 text-slate-700">{p.request}</p>
        </CrudCard>
      ))}
    </div>
  );
}

/* ---------- Homepage ---------- */
function HomepageAdmin() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/homepage").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-500">Loading…</div>;

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const service_times = [];
    for (let i = 0; i < 5; i++) {
      const n = fd.get(`st_name_${i}`), t = fd.get(`st_time_${i}`);
      if (n && t) service_times.push({ name: n, time: t });
    }
    const { data: updated } = await api.put("/admin/homepage", {
      welcome: fd.get("welcome"),
      vision: fd.get("vision"),
      mission: fd.get("mission"),
      salvation_call: fd.get("salvation_call"),
      service_times,
    });
    setData(updated);
    toast.success("Homepage updated");
  };

  return (
    <form onSubmit={submit} className="card-soft p-8 grid gap-5 max-w-3xl">
      <div><Label>Welcome message</Label><Textarea name="welcome" rows={3} defaultValue={data.welcome} /></div>
      <div><Label>Vision</Label><Textarea name="vision" rows={3} defaultValue={data.vision} /></div>
      <div><Label>Mission</Label><Textarea name="mission" rows={3} defaultValue={data.mission} /></div>
      <div><Label>Salvation call</Label><Textarea name="salvation_call" rows={2} defaultValue={data.salvation_call} /></div>
      <div>
        <Label>Service times</Label>
        <div className="mt-2 space-y-2">
          {[0,1,2,3,4].map((i) => {
            const st = data.service_times?.[i] || {};
            return (
              <div key={i} className="grid grid-cols-2 gap-2">
                <Input name={`st_name_${i}`} defaultValue={st.name || ""} placeholder="Service name" />
                <Input name={`st_time_${i}`} defaultValue={st.time || ""} placeholder="Day / Time" />
              </div>
            );
          })}
        </div>
      </div>
      <button className="btn-primary w-fit" data-testid="admin-homepage-save">Save Homepage</button>
    </form>
  );
}
